import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
import ENV from 'madow-tech/config/environment';
import FormInput from 'madow-tech/components/form/input';
import FormTextarea from 'madow-tech/components/form/textarea';
import SpaceButton from 'madow-tech/components/space-button';

interface ContactCtaSignature {
  Args?: {
    introTextKey?: string;
    subtextKey?: string;
  };
}

export default class ContactCta extends Component<ContactCtaSignature> {
  @service declare router: RouterService;
  @service declare intl: IntlService;

  @tracked email = '';
  @tracked message = '';
  @tracked loading = false;
  @tracked success = false;
  @tracked error = false;

  @action onEmail(e: Event) {
    const t = e.target as HTMLInputElement;
    this.email = t?.value ?? '';
  }

  @action onMessage(e: Event) {
    const t = e.target as HTMLTextAreaElement;
    this.message = t?.value ?? '';
  }

  @action async submit(e: Event) {
    e.preventDefault();
    if (!this.email || !this.message) return;

    this.loading = true;
    this.success = false;
    this.error = false;

    try {
      const body = new URLSearchParams({
        email: this.email,
        message: this.message,
      } as any);
      const res = await fetch(
        `${(ENV as any).API_BASE_URL || 'http://localhost:3100'}/mail/contact`,
        {
          method: 'POST',
          body,
          mode: 'cors',
        },
      );

      if (res.ok) {
        this.success = true;
        this.email = '';
        this.message = '';

        // Redirect to thank-you page - locale-agnostic route
        // The page will display content in user's preferred language via ember-intl
        try {
          // Small delay to show success state briefly before redirect
          setTimeout(() => {
            this.router.transitionTo('contact-success');
          }, 500);
        } catch (redirectError) {
          // If redirect fails, keep success state visible as fallback
          console.warn('Failed to redirect to thank-you page:', redirectError);
        }
      } else {
        this.error = true;
      }
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
    }
  }
  <template>
    <section id="contact" class="py-20 md:py-28">
      <div class="mx-auto max-w-3xl px-4 text-center">
        <h2 class="text-3xl md:text-4xl font-bold mb-4">{{#if @introTextKey}}{{t @introTextKey}}{{else}}{{t "contact.title"}}{{/if}}</h2>
        <p class="mt-0 mb-12 md:mb-16 text-zinc-300 text-base md:text-lg">{{#if @subtextKey}}{{t @subtextKey}}{{else}}{{t "contact.intro"}}{{/if}}</p>
        <form {{on "submit" this.submit}} class="grid gap-6 text-left">
          <FormInput
            @id="email"
            @type="email"
            @label={{t "contact.form.email"}}
            @placeholder={{t "contact.form.emailPlaceholder"}}
            @value={{this.email}}
            @required={{true}}
            @disabled={{this.loading}}
            @onInput={{this.onEmail}}
          />
          <FormTextarea
            @id="message"
            @label={{t "contact.form.message"}}
            @placeholder={{t "contact.form.messagePlaceholder"}}
            @value={{this.message}}
            @required={{true}}
            @disabled={{this.loading}}
            @onInput={{this.onMessage}}
          />
          <SpaceButton
            @type="submit"
            @variant="v4"
            @disabled={{this.loading}}
            @label={{if
              this.loading
              (t "contact.form.sending")
              (if this.success (t "contact.form.success") (if this.error (t "contact.form.error") (t "contact.form.submit")))
            }}
            class="!w-full"
          />
          <div class="mt-4">
            {{#if this.success}}
              <p class="text-sm text-green-400 text-center">{{t "contact.form.successMessage"}}</p>
            {{else if this.error}}
              <p class="text-sm text-red-400 text-center">{{t "contact.form.errorMessage"}}</p>
            {{else}}
              <p class="text-xs text-zinc-500 text-center">{{t "contact.form.disclaimer"}}</p>
            {{/if}}
          </div>
        </form>
      </div>
    </section>
  </template>
}
